import { asStringArray, harnessSchema, parseStoredToolPolicy, parseStoredWorkload } from "core";
import type { AasApiAdapter, UpdateAgentInput } from "../adapter";
import { isHttpError, requestJson } from "../http";
import type { AgentDetail, AgentListItem, WorkerStatusView } from "../types";
import type { BackendProfile, BackendSecret } from "./backendTypes";
import { asObject } from "./shared";

function parseHarness(value: unknown): AgentDetail["harness"] {
	return harnessSchema.catch("pi").parse(value);
}

export function mapAgentListItem(profile: BackendProfile): AgentListItem {
	return {
		name: profile.name,
		slug: profile.slug,
		model: profile.model,
		harness: parseHarness(profile.harness),
		tools: asStringArray(profile.tools),
		connections: asStringArray(profile.connections),
		sandboxClassId: profile.sandboxClassId ?? null,
		createdAt: profile.createdAt,
	};
}

export function mapAgentDetail(profile: BackendProfile, secrets: BackendSecret[]): AgentDetail {
	return {
		name: profile.name,
		slug: profile.slug,
		systemPrompt: profile.systemPrompt,
		model: profile.model,
		thinkingLevel: profile.thinkingLevel,
		// Servers running pre-whitelist code may still send null here.
		allowedModels: profile.allowedModels ?? [profile.model],
		allowedThinkingLevels: profile.allowedThinkingLevels ?? [profile.thinkingLevel],
		tools: asStringArray(profile.tools),
		harness: parseHarness(profile.harness),
		metadata: asObject(profile.metadata),
		secrets: secrets.map((secret) => ({
			key: secret.key,
			valuePreview: secret.value,
		})),
		connections: asStringArray(profile.connections),
		egressAllowedHosts: asStringArray(profile.egressAllowedHosts),
		sandboxClassId: profile.sandboxClassId ?? null,
		workload: parseStoredWorkload(profile.workload),
		toolPolicy:
			profile.toolPolicy === null || profile.toolPolicy === undefined
				? null
				: parseStoredToolPolicy(profile.toolPolicy),
	};
}

export async function loadAgent(slug: string): Promise<AgentDetail | null> {
	try {
		const profile = await requestJson<BackendProfile>(`/api/agents/${encodeURIComponent(slug)}`);
		const secrets = await requestJson<BackendSecret[]>(
			`/api/agents/${encodeURIComponent(slug)}/secrets`,
		).catch(() => []);
		return mapAgentDetail(profile, secrets);
	} catch (error) {
		if (isHttpError(error) && error.status === 404) {
			return null;
		}
		throw error;
	}
}

function agentBody(input: UpdateAgentInput) {
	return {
		name: input.name,
		slug: input.slug,
		systemPrompt: input.systemPrompt,
		// A worker has no model; the server rejects an empty one and ignores
		// whatever default it fills in for that harness.
		...(input.model ? { model: input.model } : {}),
		thinkingLevel: input.thinkingLevel,
		allowedModels: input.allowedModels,
		allowedThinkingLevels: input.allowedThinkingLevels,
		tools: input.tools,
		harness: input.harness,
		metadata: input.metadata,
		// Optional: omitted = leave unchanged server-side (partial PATCH).
		...(input.egressAllowedHosts !== undefined
			? { egressAllowedHosts: input.egressAllowedHosts }
			: {}),
		...(input.workload !== undefined ? { workload: input.workload } : {}),
		...(input.sandboxClassId !== undefined ? { sandboxClassId: input.sandboxClassId } : {}),
		...(input.toolPolicy !== undefined ? { toolPolicy: input.toolPolicy } : {}),
	};
}

export const agentsApi: AasApiAdapter["agents"] = {
	async list() {
		const agents = await requestJson<BackendProfile[]>("/api/agents");
		return agents.map(mapAgentListItem);
	},
	async get(slug) {
		return loadAgent(slug);
	},
	async workerStatus(slug) {
		return requestJson<WorkerStatusView>(`/api/agents/${encodeURIComponent(slug)}/worker`);
	},
	async resetWorker(slug) {
		await requestJson<void>(`/api/agents/${encodeURIComponent(slug)}/worker/reset`, {
			method: "POST",
		});
	},
	async create(input) {
		const created = await requestJson<BackendProfile>("/api/agents", {
			method: "POST",
			body: agentBody(input),
		});
		const detail = await loadAgent(created.slug);
		if (!detail) {
			throw new Error("Created agent could not be loaded");
		}
		return detail;
	},
	async update(slug: string, input: UpdateAgentInput) {
		const updated = await requestJson<BackendProfile>(`/api/agents/${encodeURIComponent(slug)}`, {
			method: "PATCH",
			body: agentBody(input),
		});
		const detail = await loadAgent(updated.slug);
		if (!detail) {
			throw new Error("Updated agent could not be loaded");
		}
		return detail;
	},
	async delete(slug: string) {
		await requestJson<void>(`/api/agents/${encodeURIComponent(slug)}`, { method: "DELETE" });
	},
	async setSecret(slug, key, value) {
		await requestJson<void>(`/api/agents/${encodeURIComponent(slug)}/secrets`, {
			method: "POST",
			body: { key, value },
		});
	},
	async deleteSecret(slug, key) {
		await requestJson<void>(
			`/api/agents/${encodeURIComponent(slug)}/secrets/${encodeURIComponent(key)}`,
			{ method: "DELETE" },
		);
	},
};
